import { Unsubscribe } from 'router5/dist/types/base';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IRouter } from '../../infrastructure/router';
import { RouteParams, Routes } from '../../routes-table';
import { IErrorParsing } from '../../infrastructure/error-parsing';
import { IModuleVM } from '../../infrastructure/components/module-vm';
import { IAlertsService } from '../../infrastructure/alerts';
import { ISubWindowsService } from '../../infrastructure/sub-windows';
import { IAccountDetailsDTO, IAccountsDAO } from '../../api/accounts.dao';
import { RADIX_PARAM } from '../../constants/common.constants';
import { PagingViewModel } from '../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { IAccountUsersDAO, IAccountUsersDTO, IAccountUsersListDTOInternal } from '../../api/account-users.dao';
import TrackingServiceProvider from '../../tracking-service/tracking-service-provider';
import { TRACKING_EVENTS } from '../../tracking-service/tracking-values';

class AccountUsersVM implements IModuleVM {
    @mobx.observable
    public accountUsersList: IAccountUsersDTO[];
    @mobx.observable
    public isLoading: boolean;
    private subscriptions: Unsubscribe[];
    @mobx.observable
    private account: IAccountDetailsDTO;
    @mobx.observable
    public paginationVM: PagingViewModel;

    constructor(
        @IoC.Inject private readonly router: IRouter<RouteParams> = IoC.Container.get(IRouter),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly accountsDAO: IAccountsDAO = IoC.Container.get(IAccountsDAO),
        @IoC.Inject private readonly accountUsersDAO: IAccountUsersDAO = IoC.Container.get(IAccountUsersDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider)

    ) {
        this.paginationVM = new PagingViewModel();
        mobx.reaction(this.paginationVM.getCurrentPage, this.fetchAccountUsersList);
    }

    didMount(): void {
        this.subscriptions = [];

        this.subscriptions.push(
            this.router.listen(Routes.AccountUsersList, (params: { accountId: string }) => {
                mobx.runInAction(() => {
                    this.isLoading = true;
                    this.fetchAccount(params.accountId).then(() => {
                        this.fetchAccountUsersList();
                    });
                });
            })
        );
    }

    willUnmount(): void {
        this.subscriptions.forEach(x => x());
    }

    @mobx.action
    public fetchAccountUsersList = async (): Promise<void> => {
        mobx.runInAction(() => {
            this.isLoading = true;
        });

        try {
            const params = {
                accountId: this.account.accountId,
                page: this.paginationVM.getCurrentPageForBE(),
                size: this.paginationVM.getPageSize()
            };

            const response = await this.accountUsersDAO.getAccountUsers(params);
            this.onFetchAccountUsersListSuccess(response);
        } catch (error) {
            this.onFetchAccountUsersListError(error);
        } finally {
            mobx.runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    @mobx.action
    private onFetchAccountUsersListSuccess = (response: IAccountUsersListDTOInternal): void => {
        this.accountUsersList = response.content;

        this.paginationVM.setTotalRecords(response.totalElements);
    };

    @mobx.action
    private onFetchAccountUsersListError = (error): void =>{
        this.alertsService.alert({
            message: this.errorParsingService.extractMessage(error)
        });
    };

    @mobx.action
    private fetchAccount = async (accountIdString: string): Promise<void> => {
        const accountId = parseInt(accountIdString, RADIX_PARAM);

        if (!this.account || accountId !== this.account.accountId) {
            let account;

            try {
                account = await this.accountsDAO.readAccount(accountId);
            } catch (error) {
                this.isLoading = false;
                this.router.navigate(Routes.AccountsList, {});
            }

            if (account) this.setAccount(account);
        }
    };

    @mobx.action
    private setAccount = (account: IAccountDetailsDTO): void => {
        this.account = account;
    };

    public downloadAccountUsersCSV = (): void => {
        const accountParams = {accountId: this.account.accountId};
        this.accountUsersDAO.getAccountUsersCSV(accountParams);
        this.trackingService.get().track(TRACKING_EVENTS.DOWNLOAD_ALL_ACCOUNT_USERS, {});
    };
}

export { AccountUsersVM };
